import { APP_PREFIX_PATH } from '@constants/common';
import CustomDashboardsIcon from '@developers/assets/icons/custom-dashboards.svg?react';
import EOrderApiIcon from '@developers/assets/icons/eorder-api.svg?react';
import ErpIntegrationIcon from '@developers/assets/icons/erp-integration.svg?react';
import FinancialAutomationIcon from '@developers/assets/icons/financial-automation.svg?react';
import InventoryManagementIcon from '@developers/assets/icons/inventory-management.svg?react';
import LatestDocumentationIcon from '@developers/assets/icons/latest-documentation.svg?react';
import NewProductLaunchesIcon from '@developers/assets/icons/new-product-launches.svg?react';
import RemoteControlIcon from '@developers/assets/icons/remote-control.svg?react';
import SmartIntegrationsIcon from '@developers/assets/icons/smart-integrations.svg?react';
import UpToDateIcon from '@developers/assets/icons/up-to-date.svg?react';
import VictronWebsiteApiIcon from '@developers/assets/icons/victron-website-api.svg?react';
import VrmApiIcon from '@developers/assets/icons/vrm-api.svg?react';
import WebsiteSyncIcon from '@developers/assets/icons/website-sync.svg?react';
import { ApiCard } from '@developers/components/display/cards';
import { useAppDispatch } from '@hooks/generic';
import { Stack } from '@mui/material';
import { setBreadcrumbs } from '@store/breadcrumbSlice';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export const DevelopersHome = () => {
    const intl = useIntl();
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(
            setBreadcrumbs([
                {
                    label: intl.formatMessage({ id: 'developers', defaultMessage: 'Developers' }),
                    to: APP_PREFIX_PATH + '/developers',
                },
            ])
        );
    }, [dispatch, intl]);

    return (
        <>
            <title>
                {intl.formatMessage({
                    id: 'developers.home.title',
                    defaultMessage: 'Victron Energy Professional - Developers',
                })}
            </title>
            <Stack component="main" p="16px" direction={{ xs: 'column', lg: 'row' }} spacing="16px">
                <ApiCard
                    className="flex-1"
                    title={<FormattedMessage id="developers.api.eorder" defaultMessage="E-Order API" />}
                    icon={<EOrderApiIcon />}
                    features={[
                        {
                            icon: <ErpIntegrationIcon />,
                            title: (
                                <FormattedMessage id="developers.api.erpIntegration" defaultMessage="ERP Integration" />
                            ),
                            description: (
                                <FormattedMessage
                                    id="developers.api.erpIntegration.description"
                                    defaultMessage="Instantly sync new product listings to your online store and ERP."
                                />
                            ),
                        },
                        {
                            icon: <InventoryManagementIcon />,
                            title: (
                                <FormattedMessage
                                    id="developers.api.inventoryManagement"
                                    defaultMessage="Inventory Management"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="developers.api.inventoryManagement.description"
                                    defaultMessage="Gain real-time visibility into Victron stock levels, to ensure accurate planning and fulfilment."
                                />
                            ),
                        },
                        {
                            icon: <WebsiteSyncIcon />,
                            title: (
                                <FormattedMessage
                                    id="developers.api.websiteSync"
                                    defaultMessage="Website Synchronization"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="developers.api.websiteSync.description"
                                    defaultMessage="Ensure your website / webshop always displays real-time availability and product information (images, technical data, etc)."
                                />
                            ),
                        },
                        {
                            icon: <FinancialAutomationIcon />,
                            title: (
                                <FormattedMessage
                                    id="developers.api.financialAutomation"
                                    defaultMessage="Financial Automation"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="developers.api.financialAutomation.description"
                                    defaultMessage="Always have your accurate purchasing pricing synced with your ERP."
                                />
                            ),
                        },
                    ]}
                    linkTo="api-eorder"
                />
                <ApiCard
                    className="flex-1"
                    title={<FormattedMessage id="developers.api.victronWebsite" defaultMessage="Victron Website API" />}
                    icon={<VictronWebsiteApiIcon />}
                    features={[
                        {
                            icon: <NewProductLaunchesIcon />,
                            title: (
                                <FormattedMessage
                                    id="developers.api.newProductLaunches"
                                    defaultMessage="New Product Launches"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="developers.api.newProductLaunches.description"
                                    defaultMessage="Have new product launches instantly appear on your website."
                                />
                            ),
                        },
                        {
                            icon: <UpToDateIcon />,
                            title: (
                                <FormattedMessage
                                    id="developers.api.upToDate"
                                    defaultMessage="Up to date product information"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="developers.api.upToDate.description"
                                    defaultMessage="Ensure product descriptions are always complete and up to date."
                                />
                            ),
                        },
                        {
                            icon: <LatestDocumentationIcon />,
                            title: (
                                <FormattedMessage
                                    id="developers.api.latestDocumentation"
                                    defaultMessage="Latest Documentation Access"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="developers.api.latestDocumentation.description"
                                    defaultMessage="Give your customers access to the latest data sheets and manuals."
                                />
                            ),
                        },
                    ]}
                    linkTo="api-victron"
                />
                <ApiCard
                    className="flex-1"
                    title={<FormattedMessage id="developers.api.vrm" defaultMessage="VRM API" />}
                    icon={<VrmApiIcon />}
                    features={[
                        {
                            icon: <CustomDashboardsIcon />,
                            title: (
                                <FormattedMessage
                                    id="developers.api.customDashboards"
                                    defaultMessage="Custom dashboards"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="developers.api.customDashboards.description"
                                    defaultMessage="Create your own interfaces to track voltage, current, battery status, and more."
                                />
                            ),
                        },
                        {
                            icon: <SmartIntegrationsIcon />,
                            title: (
                                <FormattedMessage
                                    id="developers.api.smartIntegrations"
                                    defaultMessage="Smart integrations"
                                />
                            ),
                            description: (
                                <FormattedMessage
                                    id="developers.api.smartIntegrations.description"
                                    defaultMessage="Connect your Victron system to home automation, IoT platforms, or data logging services."
                                />
                            ),
                        },
                        {
                            icon: <RemoteControlIcon />,
                            title: (
                                <FormattedMessage id="developers.api.remoteControl" defaultMessage="Remote control" />
                            ),
                            description: (
                                <FormattedMessage
                                    id="developers.api.remoteControl.description"
                                    defaultMessage="Turn devices on or off, switch relays, or adjust charging parameters – all from anywhere."
                                />
                            ),
                        },
                    ]}
                    linkTo="api-vrm"
                />
            </Stack>
        </>
    );
};
